import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import { PublicGuard } from "@/components/auth-guard";
import { AuthShell } from "@/components/auth-shell";
import {
  FieldErrorMessage,
  FormErrorAlert,
} from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { getErrorMessage } from "@/lib/form-errors";

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
});

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    general?: string;
  }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      setErrors({
        email: parsed.error.issues[0]?.message ?? "Enter a valid email address",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setErrors({});
      toast.success("If the email exists, a reset link has been sent.");
      navigate("/sign-in");
    } catch (err: unknown) {
      const message = getErrorMessage(err, "Failed to request password reset");
      setErrors({ general: message });
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicGuard>
      <AuthShell
        title="Forgot password"
        description="We will send a secure link to reset it."
      >
        <div className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                aria-invalid={Boolean(errors.email)}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email || errors.general) {
                    setErrors({});
                  }
                }}
                required
              />
              <FieldErrorMessage message={errors.email} />
            </div>
            <FormErrorAlert message={errors.general} />
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? "Sending..." : "Send reset link"}
            </Button>
          </form>

          <div className="text-center pt-4 border-t border-border">
            <Button
              variant="link"
              onClick={() => navigate("/sign-in")}
              className="font-medium text-foreground"
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </AuthShell>
    </PublicGuard>
  );
}
